import { ImageOff, Maximize2, Minus, Plus, X } from 'lucide-react'
import { useEffect, useId, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'

import { BUTTON_STEP, FIT, MAX_SCALE, MIN_SCALE, clampView, percentText, toggleZoomAt, zoomAt, type PhotoView } from './photoZoom'
import { useSwipeDismiss } from './useSwipeDismiss'
import './photo.css'

/** Шаг колеса мыши: меньше шага кнопки, чтобы приближение было плавным. */
const WHEEL_STEP = 1.15
/** Два касания по картинке не позже этого срока (мс) и не дальше этого друг от друга (px) - двойной тап. */
const DOUBLE_TAP_MS = 320
const DOUBLE_TAP_DISTANCE = 32
/** Сдвиг больше этого (px) - уже не тап, а перетаскивание: клик, которым оно закончилось, просмотр не закрывает. */
const TAP_SLOP = 8
/** Размер значков в просмотре, px: закрытие, «нет фото», кнопки масштаба. */
const CLOSE_ICON_SIZE = 22
const NO_PHOTO_ICON_SIZE = 24
const ZOOM_ICON_SIZE = 20
const FIT_ICON_SIZE = 18
/** Значок «нет фото» в миниатюре: доля стороны и нижняя граница, px. */
const THUMB_ICON_RATIO = 0.4
const THUMB_ICON_MIN = 14

interface Point {
  x: number
  y: number
}

const distanceOf = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y)
const middleOf = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })

/**
 * Фото на весь экран (как Lightbox старого клиента): товар видно целиком, без рамки окна. Приближают щипком, двойным тапом,
 * колесом мыши и кнопками «+» / «−» / «По размеру»; приближенное фото двигают пальцем или мышью. Закрывают кнопка «Закрыть»,
 * Esc, тап мимо фото и смах вниз (на приближенном фото смах - это сдвиг, поэтому он не закрывает) - жест не единственный способ
 * (CLAUDE.md §7). Подпись `caption` говорит, что за картинка; не загрузилась - вместо пустоты читаемое сообщение.
 *
 * Esc перехватывается на фазе захвата и дальше не идёт: под просмотром открыто окно (сканер, карточка), и одно
 * нажатие не должно закрыть заодно и его.
 */
export function PhotoViewer({
  src,
  alt,
  caption,
  onClose,
}: {
  src: string
  alt: string
  caption?: string | undefined
  onClose: () => void
}) {
  const captionId = useId()
  const close = useRef(onClose)
  const button = useRef<HTMLButtonElement>(null)
  const stage = useRef<HTMLDivElement>(null)
  const image = useRef<HTMLImageElement>(null)
  const viewRef = useRef<PhotoView>(FIT)
  const [view, setView] = useState<PhotoView>(FIT)
  const [pinching, setPinching] = useState(false)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const failed = failedSrc === src
  const pointers = useRef(new Map<number, Point>())
  const pan = useRef<{ start: Point; view: PhotoView } | null>(null)
  const pinch = useRef<{ distance: number; middle: Point; view: PhotoView } | null>(null)
  const dragged = useRef(false)
  const lastTap = useRef<{ time: number; point: Point } | null>(null)

  const commit = (next: PhotoView) => {
    viewRef.current = next
    setView(next)
  }

  /** Показанный размер картинки при масштабе 1 и её центр без сдвига: от них считаются границы и точка приближения. */
  const metrics = () => {
    const element = image.current
    if (element === null) return { width: 0, height: 0, centerX: 0, centerY: 0 }
    const rect = element.getBoundingClientRect()
    return {
      width: element.offsetWidth,
      height: element.offsetHeight,
      centerX: rect.left + rect.width / 2 - viewRef.current.x,
      centerY: rect.top + rect.height / 2 - viewRef.current.y,
    }
  }

  const zoomTo = (scale: number) => {
    const m = metrics()
    commit(zoomAt(viewRef.current, scale, 0, 0, m.width, m.height))
  }
  const zoomIn = () => zoomTo(viewRef.current.scale * BUTTON_STEP)
  const zoomOut = () => zoomTo(viewRef.current.scale / BUTTON_STEP)
  const fit = () => commit(FIT)

  // Обработчики, живущие дольше одного рендера (клавиши, колесо), берут свежие функции отсюда.
  const live = useRef({ zoomIn, zoomOut, fit, metrics, commit })
  useEffect(() => {
    live.current = { zoomIn, zoomOut, fit, metrics, commit }
  })
  useEffect(() => {
    close.current = onClose
  }, [onClose])

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null
    button.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close.current()
      } else if (event.key === '+' || event.key === '=') live.current.zoomIn()
      else if (event.key === '-') live.current.zoomOut()
      else if (event.key === '0') live.current.fit()
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      before?.focus()
    }
  }, [])

  // Колесо: нативный слушатель без пассивности - иначе страница под просмотром прокручивается вместе с ним.
  useEffect(() => {
    const element = stage.current
    if (element === null) return undefined
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const m = live.current.metrics()
      const current = viewRef.current
      const factor = event.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP
      live.current.commit(zoomAt(current, current.scale * factor, event.clientX - m.centerX, event.clientY - m.centerY, m.width, m.height))
    }
    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [])

  const zoomed = view.scale > MIN_SCALE
  const swipe = useSwipeDismiss({ axis: 'down', onDismiss: () => close.current(), disabled: zoomed || pinching })

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointers.current.size === 0) dragged.current = false
    const point = { x: event.clientX, y: event.clientY }
    pointers.current.set(event.pointerId, point)
    if (pointers.current.size === 2) {
      const [first, second] = [...pointers.current.values()]
      if (first !== undefined && second !== undefined) {
        pinch.current = { distance: Math.max(1, distanceOf(first, second)), middle: middleOf(first, second), view: viewRef.current }
        pan.current = null
        dragged.current = true
        setPinching(true)
      }
    } else if (pointers.current.size === 1) {
      pan.current = { start: point, view: viewRef.current }
    }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return
    const point = { x: event.clientX, y: event.clientY }
    pointers.current.set(event.pointerId, point)
    const m = metrics()
    const gesture = pinch.current
    if (gesture !== null && pointers.current.size === 2) {
      const [first, second] = [...pointers.current.values()]
      if (first === undefined || second === undefined) return
      const middle = middleOf(first, second)
      const scale = gesture.view.scale * (distanceOf(first, second) / gesture.distance)
      const anchored = zoomAt(gesture.view, scale, gesture.middle.x - m.centerX, gesture.middle.y - m.centerY, m.width, m.height)
      commit(clampView({ ...anchored, x: anchored.x + middle.x - gesture.middle.x, y: anchored.y + middle.y - gesture.middle.y }, m.width, m.height))
      return
    }
    const current = pan.current
    if (current === null) return
    const dx = point.x - current.start.x
    const dy = point.y - current.start.y
    if (Math.abs(dx) + Math.abs(dy) > TAP_SLOP) dragged.current = true
    if (viewRef.current.scale > MIN_SCALE) {
      commit(clampView({ scale: viewRef.current.scale, x: current.view.x + dx, y: current.view.y + dy }, m.width, m.height))
    }
  }

  const onPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.delete(event.pointerId)) return
    pinch.current = null
    setPinching(false)
    const rest = [...pointers.current.values()][0]
    if (rest !== undefined) {
      // Один палец остался после щипка: дальше он двигает фото от своего места.
      pan.current = { start: rest, view: viewRef.current }
      return
    }
    pan.current = null
    // Двойной тап - только по самой картинке; одиночный по затемнению закрывает просмотр.
    if (event.type !== 'pointerup' || dragged.current || event.target !== image.current) {
      lastTap.current = null
      return
    }
    const point = { x: event.clientX, y: event.clientY }
    const previous = lastTap.current
    if (previous !== null && event.timeStamp - previous.time < DOUBLE_TAP_MS && distanceOf(previous.point, point) < DOUBLE_TAP_DISTANCE) {
      lastTap.current = null
      const m = metrics()
      commit(toggleZoomAt(viewRef.current, point.x - m.centerX, point.y - m.centerY, m.width, m.height))
      return
    }
    lastTap.current = { time: event.timeStamp, point }
  }

  const imageStyle: CSSProperties = { transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }
  return createPortal(
    <div
      className="krp-photo-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      aria-describedby={caption === undefined ? undefined : captionId}
      onClick={() => {
        // Клик, которым закончилось перетаскивание или щипок, просмотр не закрывает.
        if (dragged.current) {
          dragged.current = false
          return
        }
        close.current()
      }}
    >
      <button
        ref={button}
        type="button"
        className="krp-photo-viewer__close"
        aria-label="Закрыть фото"
        onClick={(event) => {
          event.stopPropagation()
          close.current()
        }}
      >
        <X size={CLOSE_ICON_SIZE} aria-hidden="true" />
        <span aria-hidden="true">Закрыть</span>
      </button>
      <div ref={stage} className="krp-photo-viewer__stage" {...swipe.bind()} style={swipe.style}>
        <div
          className="krp-photo-viewer__layer"
          data-zoomed={zoomed ? 'yes' : 'no'}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        >
          {failed ? (
            <p className="krp-photo-viewer__error" role="alert" onClick={(event) => event.stopPropagation()}>
              <ImageOff size={NO_PHOTO_ICON_SIZE} aria-hidden="true" />
              <span>Картинка не загрузилась: адрес недоступен или файл повреждён. Закройте просмотр и откройте картинку ещё раз.</span>
            </p>
          ) : (
            <img
              ref={image}
              className="krp-photo-viewer__image"
              src={src}
              alt={alt}
              draggable={false}
              style={imageStyle}
              onError={() => setFailedSrc(src)}
              onClick={(event) => event.stopPropagation()}
            />
          )}
        </div>
      </div>
      <div className="krp-photo-viewer__bar" onClick={(event) => event.stopPropagation()}>
        {caption !== undefined && (
          <p id={captionId} className="krp-photo-viewer__caption">
            {caption}
          </p>
        )}
        {!failed && (
          <div className="krp-photo-viewer__zoom" role="group" aria-label="Масштаб фото">
            <button type="button" className="krp-photo-viewer__tool" aria-label="Уменьшить" disabled={view.scale <= MIN_SCALE} onClick={zoomOut}>
              <Minus size={ZOOM_ICON_SIZE} aria-hidden="true" />
            </button>
            <span className="krp-photo-viewer__scale" data-testid="photo-viewer-scale">
              {percentText(view.scale)}
            </span>
            <button type="button" className="krp-photo-viewer__tool" aria-label="Увеличить" disabled={view.scale >= MAX_SCALE} onClick={zoomIn}>
              <Plus size={ZOOM_ICON_SIZE} aria-hidden="true" />
            </button>
            <button type="button" className="krp-photo-viewer__tool krp-photo-viewer__tool--fit" aria-label="По размеру" disabled={!zoomed} onClick={fit}>
              <Maximize2 size={FIT_ICON_SIZE} aria-hidden="true" />
              <span aria-hidden="true">По размеру</span>
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Картинка с просмотром на весь экран: нажатие по ней открывает `PhotoViewer` с подписью `caption` (что за картинка). Один
 * компонент на все окна, где показывают снимки и кадры (детали поиска, сравнение, миниатюры вариантов). Не загрузилась -
 * значок «нет картинки» и слова, а не пустое место; кнопка остаётся отдельным элементом, вложенных кнопок нет.
 */
export function ZoomableImage({
  src,
  alt,
  caption,
  imageClassName,
}: {
  src: string
  /** Имя картинки для диктора; в просмотре это же заголовок окна. */
  alt: string
  /** Подпись под просмотром: что за картинка. */
  caption: string
  imageClassName?: string | undefined
}) {
  const [failed, setFailed] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  if (failed === src) {
    return (
      <span className="krp-zoomable-error" role="img" aria-label={`Картинка не загрузилась: ${alt}`}>
        <ImageOff size={NO_PHOTO_ICON_SIZE} aria-hidden="true" />
        <span>Картинка не загрузилась</span>
      </span>
    )
  }
  return (
    <>
      <button type="button" className="krp-zoomable" aria-label={`Увеличить: ${alt}`} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <img className={imageClassName} src={src} alt={alt} onError={() => setFailed(src)} />
      </button>
      {open && <PhotoViewer src={src} alt={alt} caption={caption} onClose={() => setOpen(false)} />}
    </>
  )
}

/**
 * Миниатюра фото товара: нажатие открывает фото на весь экран. Фото нет или оно не загрузилось — значок «нет фото»
 * формой, а не только цветом. Миниатюра — отдельная кнопка рядом с плашкой, не внутри неё: вложенные кнопки ломают
 * нажатие.
 */
export function ProductThumb({ src, name, size = 56 }: { src: string | null; name: string; size?: number }) {
  const [failed, setFailed] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const style = { '--krp-thumb-size': `${size}px` } as CSSProperties
  if (src === null || failed === src) {
    return (
      <span className="krp-thumb" data-empty="true" style={style} role="img" aria-label={`Нет фото: ${name}`}>
        <ImageOff size={Math.max(THUMB_ICON_MIN, Math.round(size * THUMB_ICON_RATIO))} aria-hidden="true" />
      </span>
    )
  }
  return (
    <>
      <button
        type="button"
        className="krp-thumb"
        style={style}
        aria-label={`Фото на весь экран: ${name}`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <img src={src} alt="" loading="lazy" onError={() => setFailed(src)} />
      </button>
      {open && <PhotoViewer src={src} alt={`Фото товара ${name}`} caption={`Фото товара ${name}`} onClose={() => setOpen(false)} />}
    </>
  )
}
